import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, DollarSign, AlertTriangle, RefreshCw, Loader, Wrench } from 'lucide-react';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';

interface TimelineEvent {
  eventType: string;
  description: string;
  createdAt: string;
}

interface MaintenanceDetail {
  id: number;
  category: string;
  urgency: string;
  status: string;
  displayStatus: string;
  description: string;
  propertyAddress?: string;
  createdAt: string;
  quoteAmount?: number;
  quoteStatus?: string;
  completedAt?: string;
  canReopen: boolean;
  daysUntilReopenExpires?: number;
  hasContractor?: boolean;
  slaDue?: string;
  timeline: TimelineEvent[];
}

export default function RenterMaintenanceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [request, setRequest] = useState<MaintenanceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);

  useEffect(() => {
    if (id && !isNaN(Number(id))) {
      loadRequest();
    } else if (id) {
      setError('Invalid request ID');
      setLoading(false);
    }
  }, [id]);

  const loadRequest = async () => {
    try {
      const response = await api.get<MaintenanceDetail>(`/renter/maintenance/requests/${id}`);
      setRequest(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load request');
    } finally {
      setLoading(false);
    }
  };

  const handleReopen = async () => {
    if (!reopenReason.trim()) {
      alert('Please provide a reason for reopening');
      return;
    }

    setReopening(true);
    try {
      await api.post(`/renter/maintenance/requests/${id}/reopen`, {
        reason: reopenReason
      });
      alert('Request reopened successfully');
      setShowReopenModal(false);
      navigate('/maintenance/track');
    } catch (err: any) {
      alert('Failed to reopen: ' + err.message);
    } finally {
      setReopening(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return '#F59E0B';
      case 'IN_REVIEW': return '#8B5CF6';
      case 'ASSIGNED': return '#0C4432';
      case 'SCHEDULED': return '#49c5b6';
      case 'IN_PROGRESS': return '#3B82F6';
      case 'COMPLETED': return '#10B981';
      default: return '#666';
    }
  };

  const getProgressPercentage = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return 15;
      case 'IN_REVIEW': return 30;
      case 'ASSIGNED': return 50;
      case 'SCHEDULED': return 70;
      case 'IN_PROGRESS': return 85;
      case 'COMPLETED': return 100;
      default: return 0;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#0C4432' }}></div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: '#DF6C4F' }} />
          <p className="text-xl font-semibold" style={{ color: '#222' }}>{error || 'Request not found'}</p>
          <button
            onClick={() => navigate('/maintenance/track')}
            className="mt-4 px-4 py-2 rounded-md text-white"
            style={{ backgroundColor: '#0C4432' }}
          >
            Back to Maintenance
          </button>
        </div>
      </div>
    );
  }

  const progressPercentage = getProgressPercentage(request.displayStatus);

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F8F8F8' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <PageHeader
          title={`Request #${request.id}`}
          subtitle={request.category || 'Maintenance Request'}
          icon={<Wrench className="w-8 h-8" />}
          backPath="/maintenance/track"
        />

        {/* Details Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{
                    backgroundColor: request.urgency === 'EMERGENCY' ? '#FEE' : '#F0F4F8',
                    color: request.urgency === 'EMERGENCY' ? '#DC2626' : '#0C4432'
                  }}
                >
                  {request.urgency}
                </span>
                <span
                  className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{
                    backgroundColor: getStatusColor(request.displayStatus) + '20',
                    color: getStatusColor(request.displayStatus)
                  }}
                >
                  {request.displayStatus.replace(/_/g, ' ')}
                </span>
              </div>
            </div>

            {request.canReopen && (
              <button
                onClick={() => setShowReopenModal(true)}
                className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-gray-50"
                style={{ borderColor: '#49c5b6', color: '#49c5b6' }}
              >
                <RefreshCw className="w-4 h-4" />
                Reopen Request
              </button>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-2" style={{ color: '#666' }}>
              <span>Progress</span>
              <span>{progressPercentage}%</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${progressPercentage}%`,
                  backgroundColor: getStatusColor(request.displayStatus)
                }}
              />
            </div>
          </div>

          {/* Details */}
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-1" style={{ color: '#666' }}>Category</p>
              <p className="font-semibold" style={{ color: '#222' }}>{request.category}</p>
            </div>

            {request.propertyAddress && (
              <div className="flex items-start gap-2">
                <MapPin className="w-5 h-5 mt-0.5" style={{ color: '#666' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: '#666' }}>Property</p>
                  <p style={{ color: '#222' }}>{request.propertyAddress}</p>
                </div>
              </div>
            )}

            <div>
              <p className="text-sm font-medium mb-1" style={{ color: '#666' }}>Description</p>
              <p style={{ color: '#222' }}>{request.description}</p>
            </div>

            <div className="flex items-start gap-2">
              <Clock className="w-5 h-5 mt-0.5" style={{ color: '#666' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: '#666' }}>Submitted</p>
                <p style={{ color: '#222' }}>{new Date(request.createdAt).toLocaleString()}</p>
              </div>
            </div>

            {request.quoteAmount && (
              <div className="p-4 rounded-md" style={{ backgroundColor: '#E8F5F3' }}>
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-5 h-5" style={{ color: '#0C4432' }} />
                  <p className="text-sm font-medium" style={{ color: '#666' }}>Estimated Cost</p>
                </div>
                <p className="text-2xl font-bold" style={{ color: '#0C4432' }}>
                  ${request.quoteAmount.toFixed(2)}
                </p>
                {request.quoteStatus && (
                  <p className="text-sm mt-1" style={{ color: '#666' }}>
                    Status: {request.quoteStatus.replace(/_/g, ' ')}
                  </p>
                )}
              </div>
            )}

            {request.canReopen && request.daysUntilReopenExpires !== undefined && (
              <div className="p-4 rounded-md" style={{ backgroundColor: '#FEF3C7' }}>
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCw className="w-5 h-5" style={{ color: '#F59E0B' }} />
                  <p className="font-medium" style={{ color: '#F59E0B' }}>Can Reopen</p>
                </div>
                <p className="text-sm" style={{ color: '#666' }}>
                  You have {request.daysUntilReopenExpires} days remaining to reopen this request if the issue persists.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#0C4432' }}>Timeline</h2>
          <div className="space-y-4">
            {request.timeline.map((event, index) => (
              <div key={index} className="flex gap-4">
                <div
                  className="flex-shrink-0 w-2 h-2 rounded-full mt-2"
                  style={{ backgroundColor: '#49c5b6' }}
                />
                <div className="flex-1">
                  <p className="font-medium" style={{ color: '#0C4432' }}>
                    {event.eventType.replace(/_/g, ' ')}
                  </p>
                  <p className="text-sm" style={{ color: '#666' }}>
                    {event.description}
                  </p>
                  <p className="text-xs mt-1" style={{ color: '#999' }}>
                    {new Date(event.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reopen Modal */}
        {showReopenModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#0C4432' }}>
                Reopen Maintenance Request
              </h3>

              <p className="text-sm mb-4" style={{ color: '#666' }}>
                Please explain why you need to reopen this request:
              </p>

              <textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="The issue is still occurring because..."
                className="w-full px-3 py-2 border rounded-md mb-4"
                rows={4}
                required
              />

              <div className="flex gap-2">
                <button
                  onClick={handleReopen}
                  disabled={reopening || !reopenReason.trim()}
                  className="flex-1 py-2 px-4 rounded-md text-white disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#49c5b6' }}
                >
                  {reopening ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Reopening...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5" />
                      Reopen Request
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowReopenModal(false)}
                  className="px-4 py-2 border rounded-md"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

